import React, { useCallback, useMemo, useState } from 'react';
import { Icon } from 'glints-aries/lib/@next';
import { Neutral } from 'glints-aries/lib/@next/utilities/colors';
import { last, startsWith } from 'lodash';
import { capitalize, snakeCase } from 'lodash-es';
import { useRouter } from 'next/router';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { showCloseableNotificationWithType } from 'src/actions/notifications';
import { URL_STATE } from 'src/common/constants';
import useMount from 'src/common/hooks/useMount';
import { ROUTES } from 'src/common/routes';
import {
  trackApplyJobClicked,
  trackExternalLinkContinued,
} from 'src/common/tracking/trackingUtils';
import { getJobCategory } from 'src/common/utils/hierarchicalJobCategoryHelpers';
import { StringUtils } from 'src/common/utils/stringUtils';
import { isURL } from 'src/common/utils/urlUtils';
import { SourcedJob } from 'src/global/models/SourcedJob';
import { JobTypeMessageMap } from 'src/modules/Opportunities/pages/Explore/Components/FilterSection/messages';
import * as S from 'src/modules/Opportunity/Components/Buttons/ApplyButton.sc';
import LeaveGlintsConfirmation from 'src/modules/Opportunity/Components/LeaveGlintsConfirmation/LeaveGlintsConfirmation';
import { JobSource } from 'src/modules/Opportunity/interface';
import {
  getApplySourcedJobLinkProps,
  getSourcedJobLinkProps,
} from 'src/modules/SourcedJobs/utils';
import { getUserEntity } from 'src/selectors/user';

interface Props {
  sourcedJob: SourcedJob;
  authenticated: boolean;
  isTopFold?: boolean;
}

export const SourcedJobApplyButton: React.FC<
  React.PropsWithChildren<Props>
> = ({ sourcedJob, authenticated, isTopFold = false }) => {
  const [isLeaveGlintsConfirmationOpen, setIsLeaveGlintsConfirmationOpen] =
    useState(false);
  const router = useRouter();
  const dispatch = useDispatch();
  const me = useSelector(getUserEntity);

  const redirectToSignUp = useCallback(async () => {
    const queryParams = router.query;
    const applyLinkProps = getApplySourcedJobLinkProps(sourcedJob, queryParams);

    await router.push({
      pathname: '/signup',
      query: {
        ...router.query,
        next: `/${ROUTES.sourcedJob}`,
        nextAs: applyLinkProps.as,
      },
    });
  }, [sourcedJob, router]);

  const removeApplyFromUrl = useCallback(async () => {
    const queryParams = router.query;
    const linkProps = getSourcedJobLinkProps(sourcedJob);
    await router.replace(
      {
        ...linkProps.href,
        query: {
          ...linkProps.href.query,
          ...queryParams,
        },
      },
      linkProps.as,
      { shallow: true }
    );
  }, [sourcedJob, router]);

  const getIsApplyURL = useCallback(() => {
    const splitPathname = router.asPath.split('/');
    return startsWith(last(splitPathname), URL_STATE.apply);
  }, [router.asPath]);

  const handleURLChecking = useCallback(async () => {
    const isApplyURL = getIsApplyURL();

    if (isApplyURL && !authenticated) {
      await redirectToSignUp();
    } else if (isApplyURL && isTopFold) {
      setIsLeaveGlintsConfirmationOpen(true);
      await removeApplyFromUrl();
    }
  }, [
    getIsApplyURL,
    authenticated,
    isTopFold,
    redirectToSignUp,
    removeApplyFromUrl,
  ]);

  useMount(() => {
    handleURLChecking();
  });

  const trackingParams = useMemo(() => {
    const {
      jobCategoryName,
      jobCategoryId,
      jobSubCategoryName,
      jobRoleName,
      jobSubCategoryId,
      jobRoleCategoryId,
    } = getJobCategory(sourcedJob.jobCategory);

    const jobType = snakeCase(
      capitalize(
        (sourcedJob.type &&
          JobTypeMessageMap[sourcedJob.type]?.props?.defaultMessage) ??
          ''
      )
    );

    return {
      companyId: sourcedJob.company?.id,
      companyName: sourcedJob.company?.name,
      jobCategoryName,
      jobSubCategoryName,
      jobRoleName,
      jobCategoryId,
      jobSubCategoryId,
      jobRoleCategoryId,
      jobType,
      jobSource: JobSource.SOURCED,
    };
  }, [sourcedJob.jobCategory, sourcedJob.type, sourcedJob.company]);

  const onApplyButtonClicked = async () => {
    trackApplyJobClicked({
      userId: me?.id,
      isExternal: true,
      isSourced: true,
      jobId: sourcedJob.id,
      jobCategory: sourcedJob.jobCategory,
      minSalary: sourcedJob.minSalary,
      maxSalary: sourcedJob.maxSalary,
      jobType: sourcedJob.type,
      companyName: sourcedJob.company?.name,
    });
    if (authenticated) {
      setIsLeaveGlintsConfirmationOpen(true);
    } else {
      await redirectToSignUp();
    }
  };

  const onExternalUrlApplyCancel = () => {
    setIsLeaveGlintsConfirmationOpen(false);
  };

  const onExternalUrlApplyConfirm = () => {
    const isExternalUrlValid = isURL(sourcedJob.externalApplyURL);

    if (isExternalUrlValid) {
      trackExternalLinkContinued({
        userId: me?.id,
        isExternal: true,
        isSourced: true,
        jobId: sourcedJob.id,
        jobCategory: sourcedJob.jobCategory,
        minSalary: sourcedJob.minSalary,
        maxSalary: sourcedJob.maxSalary,
        jobType: sourcedJob.type,
        companyName: sourcedJob.company?.name,
      });
      window.open(sourcedJob.externalApplyURL, '_blank');
    } else {
      dispatch(
        showCloseableNotificationWithType({
          type: 'danger',
          message: (
            <FormattedMessage
              id="text-link-broken"
              defaultMessage="The link is broken. <ReportLink>Report</ReportLink>"
              values={{
                ReportLink: (text) => (
                  <a
                    href="https://help.glints.com/hc/en-us/requests/new"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {text}
                  </a>
                ),
              }}
            />
          ),
        })
      );
    }
    setIsLeaveGlintsConfirmationOpen(false);
  };

  return (
    <>
      <LeaveGlintsConfirmation
        isOpen={isLeaveGlintsConfirmationOpen}
        handleConfirmLeaveGlints={onExternalUrlApplyConfirm}
        handleCancelLeaveGlints={onExternalUrlApplyCancel}
        trackingParams={{
          jobId: sourcedJob.id,
          formattedJobType: trackingParams.jobType,
          companyId: trackingParams.companyId,
          companyName: trackingParams.companyName,
          jobSource: trackingParams.jobSource,
          jobCategoryName: trackingParams.jobCategoryName,
          jobSubCategoryName: trackingParams.jobSubCategoryName,
          jobRoleName: trackingParams.jobRoleName,
          jobCategoryId: trackingParams.jobCategoryId,
          jobSubCategoryId: trackingParams.jobSubCategoryId,
          jobRoleCategoryId: trackingParams.jobRoleCategoryId,
        }}
      />
      <S.ApplyButton>
        <S.Button
          variant={'solid-blue'}
          block={true}
          onClick={onApplyButtonClicked}
          data-glints-tracking-element-name="apply_job_button"
          data-gtm-job-type={trackingParams.jobType}
          data-gtm-job-id={sourcedJob.id}
          data-gtm-company-id={trackingParams.companyId}
          data-gtm-company-name={trackingParams.companyName}
          data-gtm-job-source={StringUtils.toSnakeCase(
            trackingParams.jobSource
          )}
          data-gtm-job-category={trackingParams.jobCategoryName}
          data-gtm-job-sub-category={trackingParams.jobSubCategoryName}
          data-gtm-job-role={trackingParams.jobRoleName}
          data-glints-tracking-job-category={trackingParams.jobCategoryId}
          data-glints-tracking-job-sub-category={
            trackingParams.jobSubCategoryId
          }
          data-glints-tracking-job-role={trackingParams.jobRoleCategoryId}
          startIcon={
            <Icon
              name="ri-global-line"
              style={{
                fill: Neutral.B100,
                width: '20px',
                height: '20px',
                marginRight: 0,
              }}
            />
          }
        >
          <FormattedMessage id="job.apply.external" defaultMessage="Apply" />
        </S.Button>
      </S.ApplyButton>
    </>
  );
};
